import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FileText, Layers } from 'lucide-react';
import { vendorCostAnalysisApi } from '../../services/vendorCostAnalysisApi';
import { TransactionDrillDownModal } from './TransactionDrillDownModal';
import { ContextMenu } from '../ui/ContextMenu';
import type { VCAFilters, VCAOverviewData, VCAViewMode, VCATopVendor } from '../../types/vendorCostAnalysis';

interface Props {
    filters: VCAFilters;
    selectedYear: number;
    refreshKey: number;
    viewMode: VCAViewMode;
    onFilterChange: (updates: Partial<VCAFilters>) => void;
}

function formatEUR(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

function formatPct(current: number, previous: number): { pct: string; positive: boolean } {
    const delta = current - previous;
    const pct = previous !== 0 ? ((delta / previous) * 100).toFixed(1) : current > 0 ? '100.0' : '0.0';
    return { pct, positive: delta >= 0 };
}

const TREND_OPTIONS = [
    { value: 12, label: '12M' },
    { value: 24, label: '24M' },
    { value: 36, label: '36M' },
];

export function OverviewTab({ filters, selectedYear, refreshKey, viewMode, onFilterChange }: Props) {
    const [data, setData] = useState<VCAOverviewData | null>(null);
    const [topVendorsStable, setTopVendorsStable] = useState<VCATopVendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [trendMonths, setTrendMonths] = useState(12);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: { label: string; icon: React.ReactNode; onClick: () => void }[] } | null>(null);
    const [drillDown, setDrillDown] = useState<{ vendorID?: string; categoryCode?: string; dateFrom?: string; dateTo?: string; title: string } | null>(null);
    // Track which filters should trigger a top-10 refresh (not vendorID)
    const topRefreshKey = useRef('');

    useEffect(() => {
        setLoading(true);
        setError(null);

        const maxMonth = selectedYear < new Date().getFullYear()
            ? 12
            : new Date().getMonth();

        // Determine if top-10 needs refresh (everything except vendor/group selection)
        const newTopKey = `${filters.company}|${filters.dateFrom}|${filters.dateTo}|${filters.categoryCode}|${selectedYear}|${viewMode}|${refreshKey}`;
        const shouldRefreshTop = topRefreshKey.current !== newTopKey;

        // Main data fetch (with vendor/group filter for KPIs + trend)
        const mainPromise = vendorCostAnalysisApi.getOverview({
            year: selectedYear,
            maxMonth,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            company: filters.company || undefined,
            vendorIDs: viewMode === 'vendor' && filters.vendorIDs.length > 0 ? filters.vendorIDs : undefined,
            groupVendorIDs: viewMode === 'vendorGroup' && filters.groupVendorIDs.length > 0 ? filters.groupVendorIDs : undefined,
            categoryCode: filters.categoryCode || undefined,
            trendMonths,
            viewMode,
        });

        // Top-10 fetch (WITHOUT vendor/group filter — always shows full top-10)
        const topPromise = shouldRefreshTop
            ? vendorCostAnalysisApi.getOverview({
                year: selectedYear,
                maxMonth,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                company: filters.company || undefined,
                categoryCode: filters.categoryCode || undefined,
                trendMonths: 12,
                viewMode,
            })
            : null;

        Promise.allSettled([mainPromise, topPromise].filter(Boolean))
            .then(results => {
                const mainResult = results[0];
                if (mainResult.status === 'fulfilled') {
                    setData(mainResult.value as VCAOverviewData);
                } else {
                    setError((mainResult.reason as Error).message);
                }

                if (topPromise && results[1]?.status === 'fulfilled') {
                    setTopVendorsStable((results[1].value as VCAOverviewData).topVendors);
                    topRefreshKey.current = newTopKey;
                }
            })
            .finally(() => setLoading(false));
    }, [filters, selectedYear, refreshKey, trendMonths, viewMode]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (error) return <div className="text-red-600 p-4">{error}</div>;
    if (!data) return null;

    const currentYearData = data.costByYear.find(r => r.year === selectedYear);
    const prevYear = data.costByYear.find(r => r.year === selectedYear - 1);
    const prev2Year = data.costByYear.find(r => r.year === selectedYear - 2);

    const costDelta = formatPct(currentYearData?.totalEUR || 0, prevYear?.totalEUR || 0);
    const docDelta = formatPct(currentYearData?.documentCount || 0, prevYear?.documentCount || 0);
    const vendorDelta = formatPct(currentYearData?.vendorCount || 0, prevYear?.vendorCount || 0);

    // Trend chart data with year-based coloring
    const currentYearStr = String(selectedYear);
    const chartData = data.trend.map(t => ({
        period: t.period,
        totalEUR: t.totalEUR,
        isCurrentYear: t.period.startsWith(currentYearStr),
    }));

    // Top vendors bar data (stable — not affected by vendor/group filter)
    const topVendorsData = topVendorsStable.map(v => ({
        name: v.vendorName.length > 25 ? v.vendorName.slice(0, 25) + '...' : v.vendorName,
        fullName: v.vendorName,
        totalEUR: v.totalEUR,
        vendorID: v.vendorID,
        companyCode: v.companyCode,
    }));

    // Click on a top-10 bar toggles that vendor/group in the multi-select filter.
    const handleTopVendorClick = (entry: typeof topVendorsData[0]) => {
        if (viewMode === 'vendorGroup') {
            const next = filters.groupVendorIDs.includes(entry.vendorID)
                ? filters.groupVendorIDs.filter(id => id !== entry.vendorID)
                : [...filters.groupVendorIDs, entry.vendorID];
            onFilterChange({ groupVendorIDs: next });
        } else {
            const next = filters.vendorIDs.includes(entry.vendorID)
                ? filters.vendorIDs.filter(id => id !== entry.vendorID)
                : [...filters.vendorIDs, entry.vendorID];
            onFilterChange({ vendorIDs: next });
        }
    };

    const activeSelection = viewMode === 'vendorGroup' ? filters.groupVendorIDs : filters.vendorIDs;
    const hasSelection = activeSelection.length > 0;

    const findBarIndex = (e: React.MouseEvent): number => {
        const target = e.target as SVGElement;
        const rect = target.closest?.('g.recharts-bar-rectangle');
        if (!rect) return -1;
        const parent = rect.parentElement;
        if (!parent) return -1;
        return Array.from(parent.children).indexOf(rect);
    };

    const handleTopChartContextMenu = (e: React.MouseEvent) => {
        const idx = findBarIndex(e);
        if (idx < 0 || idx >= topVendorsData.length) return;
        e.preventDefault();
        const v = topVendorsStable[idx];
        setContextMenu({ x: e.clientX, y: e.clientY, items: [
            {
                label: 'Show All Transactions',
                icon: <FileText className="w-4 h-4" />,
                onClick: () => setDrillDown({ vendorID: v.vendorID, title: `Transactions: ${v.vendorName}` }),
            },
            {
                label: 'Show by Category',
                icon: <Layers className="w-4 h-4" />,
                onClick: () => setDrillDown({ vendorID: v.vendorID, title: `By Category: ${v.vendorName}` }),
            },
        ]});
    };

    const handleTrendContextMenu = (e: React.MouseEvent) => {
        const idx = findBarIndex(e);
        if (idx < 0 || idx >= chartData.length) return;
        e.preventDefault();
        const period = chartData[idx].period; // yyyy-MM
        const [y, m] = period.split('-').map(Number);
        const monthStart = `${y}-${String(m).padStart(2, '0')}-01`;
        const monthEnd = `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
        const monthLabel = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        setContextMenu({ x: e.clientX, y: e.clientY, items: [
            {
                label: `Show Transactions for ${monthLabel}`,
                icon: <FileText className="w-4 h-4" />,
                onClick: () => setDrillDown({ dateFrom: monthStart, dateTo: monthEnd, title: `Transactions: ${monthLabel}` }),
            },
        ]});
    };

    return (
        <div className="space-y-6">
            {/* KPI Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Total Cost EUR</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatEUR(currentYearData?.totalEUR || 0)}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={costDelta.positive ? 'text-red-600' : 'text-green-600'}>
                            {costDelta.positive ? '+' : ''}{costDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                        {selectedYear - 1}: {formatEUR(prevYear?.totalEUR || 0)} | {selectedYear - 2}: {formatEUR(prev2Year?.totalEUR || 0)}
                    </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Documents</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatEUR(currentYearData?.documentCount || 0)}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={docDelta.positive ? 'text-blue-600' : 'text-gray-600'}>
                            {docDelta.positive ? '+' : ''}{docDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Active Vendors</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{currentYearData?.vendorCount || 0}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={vendorDelta.positive ? 'text-blue-600' : 'text-gray-600'}>
                            {vendorDelta.positive ? '+' : ''}{vendorDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Avg Cost per Vendor</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">
                        {currentYearData && currentYearData.vendorCount > 0
                            ? formatEUR(Math.round(currentYearData.totalEUR / currentYearData.vendorCount))
                            : '0'} EUR
                    </div>
                    <div className="mt-1 text-xs text-gray-400">All amounts in EUR</div>
                </div>
            </div>

            {/* Trend Chart */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Monthly Cost Trend</h3>
                    <div className="flex border rounded-md overflow-hidden">
                        {TREND_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setTrendMonths(opt.value)}
                                className={`px-3 py-1 text-xs ${
                                    trendMonths === opt.value
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="h-80" onContextMenu={handleTrendContextMenu}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={1}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={trendMonths <= 12 ? 0 : trendMonths <= 24 ? 1 : 2} />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatEUR(v)} />
                            <Tooltip
                                formatter={(value) => formatEUR(Number(value)) + ' EUR'}
                            />
                            <Bar dataKey="totalEUR" name="Cost EUR" radius={[2, 2, 0, 0]}>
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={index}
                                        fill={entry.isCurrentYear ? '#3b82f6' : '#d1d5db'}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Top 10 Vendors / Groups — always visible, selected one highlighted */}
            {topVendorsData.length > 0 && (
                <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Top 10 {viewMode === 'vendorGroup' ? 'Vendor Groups' : 'Vendors'} by Cost
                        {hasSelection && (
                            <span className="text-blue-600 ml-2 text-xs font-normal">
                                (click bars to toggle selection)
                            </span>
                        )}
                    </h3>
                    <div className="h-80" onContextMenu={handleTopChartContextMenu}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topVendorsData} layout="vertical" margin={{ left: 120 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => formatEUR(v)} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                                <Tooltip
                                    formatter={(value) => formatEUR(Number(value)) + ' EUR'}
                                />
                                <Bar
                                    dataKey="totalEUR"
                                    name="Cost EUR"
                                    radius={[0, 2, 2, 0]}
                                    cursor="pointer"
                                    onClick={(_data: unknown, idx: number) => handleTopVendorClick(topVendorsData[idx])}
                                >
                                    {topVendorsData.map((entry, index) => {
                                        const isSelected = activeSelection.includes(entry.vendorID);
                                        return (
                                            <Cell
                                                key={index}
                                                fill={!hasSelection ? '#6366f1' : isSelected ? '#4f46e5' : '#e0e7ff'}
                                                opacity={!hasSelection ? 1 : isSelected ? 1 : 0.4}
                                            />
                                        );
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Context Menu */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenu.items}
                    onClose={() => setContextMenu(null)}
                />
            )}

            {/* Drill-Down Modal — merges active filters with drill-down specifics */}
            {drillDown && (
                <TransactionDrillDownModal
                    isOpen={true}
                    onClose={() => setDrillDown(null)}
                    title={drillDown.title}
                    dateFrom={drillDown.dateFrom || filters.dateFrom}
                    dateTo={drillDown.dateTo || filters.dateTo}
                    company={filters.company || undefined}
                    // A specific drill-down vendorID (context-menu, bar click) wins; otherwise
                    // pass the page-wide selection through to keep the modal consistent.
                    vendorID={drillDown.vendorID || undefined}
                    vendorIDs={!drillDown.vendorID && viewMode === 'vendor' && filters.vendorIDs.length > 0 ? filters.vendorIDs : undefined}
                    groupVendorIDs={viewMode === 'vendorGroup' && filters.groupVendorIDs.length > 0 ? filters.groupVendorIDs : undefined}
                    categoryCode={drillDown.categoryCode || filters.categoryCode || undefined}
                />
            )}
        </div>
    );
}
